import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import PropertyDashboard from './PropertyDashboard';
import {
  RotateCcw,
  Maximize2,
  ArrowRight,
  ArrowLeftRight,
  Circle,
  CircleDashed,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';

export default function LeftPanel({
  categories,
  focusedCategory,
  onCategoryFocus,
  showUsedOnly,
  onShowUsedOnly,
  onResetView,
  onFitToView,
  stackData,
  selectedProperty,
  collapsed,
  onToggleCollapse,
  isMobileDrawer,
  token,
  width,
  onEditProfile,
}) {
  const usedCount = stackData?.used_platforms || 0;
  const totalCount = stackData?.total_platforms || 0;

  // Count platforms per category that are used
  const categoryCounts = {};
  if (stackData?.nodes) {
    stackData.nodes.forEach(n => {
      if (!categoryCounts[n.category_id]) {
        categoryCounts[n.category_id] = { total: 0, used: 0 };
      }
      categoryCounts[n.category_id].total++;
      if (n.used) categoryCounts[n.category_id].used++;
    });
  }

  if (collapsed) {
    return (
      <div className="left-panel-wrapper border-r bg-card/60 backdrop-blur shrink-0 flex flex-col items-center pt-3 no-print" style={{ width: '40px' }}>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onToggleCollapse}
          data-testid="panel-expand-button"
        >
          <PanelLeftOpen className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className={`left-panel-wrapper border-r bg-card/60 backdrop-blur shrink-0 flex flex-col no-print ${isMobileDrawer ? 'w-full h-full border-r-0' : ''}`} style={isMobileDrawer ? {} : { width: `${width || 280}px` }}>
      {/* Collapse button */}
      <div className="flex items-center justify-between px-4 pt-3 pb-1">
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Controls</span>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onToggleCollapse}
          data-testid="panel-collapse-button"
        >
          <PanelLeftClose className="h-4 w-4" />
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="px-4 pb-4 space-y-5">
          {/* Stats */}
          {selectedProperty !== 'overall' && (
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Property Stats</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {usedCount}
                </span>
                <span className="text-sm text-muted-foreground">of {totalCount} platforms active</span>
              </div>
            </div>
          )}

          {/* Filter Controls */}
          {selectedProperty !== 'overall' && (
            <div className="flex items-center gap-2">
              <Switch
                id="used-only"
                checked={showUsedOnly}
                onCheckedChange={onShowUsedOnly}
                data-testid="toggle-used-only"
              />
              <Label htmlFor="used-only" className="text-xs">Show active only</Label>
            </div>
          )}

          {/* Property Dashboard */}
          {selectedProperty !== 'overall' && token && (
            <PropertyDashboard
              propertyId={selectedProperty}
              token={token}
              onEditProfile={onEditProfile}
            />
          )}

          <Separator />

          {/* Categories — text wraps instead of truncating */}
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Category Legend</h3>
            <div className="space-y-1">
              {categories.map(cat => {
                const counts = categoryCounts[cat.id] || { total: 0, used: 0 };
                const isFocused = focusedCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    data-testid={`category-filter-${cat.id}`}
                    onClick={() => onCategoryFocus(cat.id)}
                    className={`w-full flex items-start gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors ${
                      isFocused
                        ? 'bg-accent/80 ring-1 ring-ring/20'
                        : 'hover:bg-muted/60'
                    }`}
                  >
                    <div
                      className="w-3 h-3 rounded-full shrink-0 border mt-0.5"
                      style={{
                        backgroundColor: isFocused ? cat.color : `${cat.color}33`,
                        borderColor: cat.color
                      }}
                    />
                    <span className="text-xs font-medium flex-1 leading-snug break-words">{cat.name}</span>
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 font-mono shrink-0">
                      {selectedProperty !== 'overall'
                        ? `${counts.used}/${counts.total}`
                        : counts.total
                      }
                    </Badge>
                  </button>
                );
              })}
            </div>
          </div>

          <Separator />

          {/* Legend */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Legend</h3>
            <div className="space-y-2.5 text-xs text-muted-foreground">
              <div className="flex items-center gap-2.5">
                <ArrowLeftRight className="h-3.5 w-3.5 text-foreground/50 shrink-0" />
                <span>Two-way integration</span>
              </div>
              <div className="flex items-center gap-2.5">
                <ArrowRight className="h-3.5 w-3.5 text-foreground/30 shrink-0" />
                <span>One-way integration</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Circle className="h-3.5 w-3.5 text-foreground/60 shrink-0" />
                <span>Active platform</span>
              </div>
              <div className="flex items-center gap-2.5">
                <CircleDashed className="h-3.5 w-3.5 text-foreground/20 shrink-0" />
                <span>Unused platform</span>
              </div>
            </div>
          </div>

          <Separator />

          {/* Quick Actions */}
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Quick Actions</h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1 text-xs gap-1.5" onClick={onResetView}>
                <RotateCcw className="h-3 w-3" /> Reset
              </Button>
              <Button variant="outline" size="sm" className="flex-1 text-xs gap-1.5" onClick={onFitToView}>
                <Maximize2 className="h-3 w-3" /> Fit
              </Button>
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
